import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import homeBanner from "../../assets/homeBannner.png";
import { IoBagCheckOutline } from "react-icons/io5";
import { FaSearch } from "react-icons/fa";
import AwakenFinancialJourneyLOGO from "../../assets/awakenFinancialJourneyLOGO.png";
import { apiGet } from "../../api/api_methods";
import { useStores } from "../../stores/index";
import Loader from "../reusableComponents/loader";
import { rolesWithId } from "../../constants/constants";
import fileDocument from "../../assets/fileDocument.png";

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid Date";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (error) {
    return "Invalid Date";
  }
};

function AllAwakens() {
  const { clientsStore } = useStores();
  const navigate = useNavigate();

  const [load, setLoad] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const fetchMetrics = async () => {
        setLoad(true);
        try {
          const user = clientsStore.user;
          let api;
          if (user.role === rolesWithId.agent) {
            // Agent should only see their assigned clients
            api = `/adminPanel/plan-type-metrics?agentId=${user._id}`;
          } else if (user.role === rolesWithId.admin) {
            // Admin sees all metrics
            api = `/adminPanel/plan-type-metrics`;
          } else {
            // Unknown role, don't fetch metrics
            console.warn("Unknown user role:", user.role);
            setLoad(false);
            return;
          }
          
          const apiResponse = await apiGet(api);
          if (apiResponse.status === 200) {
            clientsStore.setPlanTypeMetrics(apiResponse.body.data);
            setLoad(false);
          } else {
            alert("Failed to fetch clients data try again later");
            console.error(
              "Failed to fetch clients data:",
              apiResponse?.message
            );
            setLoad(false);
          }
        } catch (error) {
          alert(error);
          console.error("Error fetching clients data:", error);
          setLoad(false);
        }
      };

      const fetchAgents = async () => {
        // Only fetch agents if user is admin
        if (clientsStore.user.role !== rolesWithId.admin) {
          return;
        }
        
        setLoad(true);
        const agentRoleId = "6720e9990ca50db671add43e";
        try {
          const apiResponse = await apiGet(
            `/users/get-users?role=${agentRoleId}`
          );
          console.log("apiResponse", apiResponse);
          if (apiResponse.status === 200) {
            clientsStore.setAgents(apiResponse.body.data);
            setLoad(false);
          } else {
            console.error(
              "Failed to fetch clients data:",
              apiResponse?.message
            );
            setLoad(false);
          }
        } catch (error) {
          console.error("Error fetching clients data:", error);
          setLoad(false);
        }
      };
      await fetchMetrics();
      await fetchAgents();
      setLoad(false);
    };

    fetchData();
  }, [clientsStore]);

  const planTypes = ["basic", "comprehensive", "zeroToOne", "wealthManagement"];

  const getGreeting = () => {
    const currentHour = new Date().getHours();
    if (currentHour < 12) {
      return "Good Morning";
    } else if (currentHour < 18) {
      return "Good Afternoon";
    } else {
      return "Good Evening";
    }
  };

  // Search function to fetch all clients regardless of plan type
  const searchAllClients = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    try {
      setSearchLoading(true);
      setIsSearching(true);
      const user = clientsStore.user;
      
      // Fetch clients from all plan types using the same API as settings
      const planTypes = ['comprehensive', 'term', 'health', 'pension', 'basic', 'zeroToOne', 'wealthManagement'];
      let allClients = [];

      // Fetch clients from each plan type
      for (const planType of planTypes) {
        try {
          let apiPath;
          if (user.role === rolesWithId.agent) {
            apiPath = `/adminPanel/get-clients?planType=${planType}&status=&agentId=${user._id}&page=1&limit=1000`;
          } else {
            apiPath = `/adminPanel/get-clients?planType=${planType}&status=&page=1&limit=1000`;
          }

          const response = await apiGet(apiPath);
          
          if (response.status === 200 && response.body.success) {
            const clients = response.body.data?.clients || response.body.data || [];
            allClients = allClients.concat(clients);
          }
        } catch (error) {
          console.error(`Error fetching clients for planType ${planType}:`, error);
        }
      }

      console.log("All clients fetched for search:", allClients.length);
      
      // Filter clients based on search query
      const searchTerm = query.toLowerCase().trim();
      const filtered = allClients.filter((client) => {
        const personal = client.awareness?.personalDetails || client.personalDetails || {};
        const plan = client.awareness?.planDetails || client.planDetails || {};
        
        const searchFields = [
          personal.name,
          personal.fullName,
          client.name,
          client.fullName,
          personal.email,
          client.email,
          String(personal.phone || personal.mobile || personal.mobileNumber || client.phone || client.mobile || ""),
          plan.planType,
          client.planType,
          String(plan.planYear || client.planYear || ""),
          plan.planDuration,
          client.planDuration,
          client.factFinderId,
          client.factFinderUId,
          client.uId,
          client.uid,
          client._id,
          personal.pan || client.pan || client.panNumber || "",
        ];

        return searchFields.some(
          (value) =>
            typeof value === "string" &&
            value.toLowerCase().includes(searchTerm)
        );
      });

      setSearchResults(filtered);
    } catch (error) {
      console.error("Error searching clients:", error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [clientsStore.user]);

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim()) {
        searchAllClients(searchQuery);
      } else {
        setSearchResults([]);
        setIsSearching(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchAllClients]);

  return (
    <div className=" relative w-full h-screen overflow-y-auto bg-white-100">
      <Loader loader={load} text={"Loading..."} />
      <div className=" relative w-full">
        <img
          src={homeBanner}
          alt="homeBanner"
          className="mt-0 h-[25vh] object-cover object-center w-full"
        />
        <div className="absolute inset-y-0 right-3 flex items-center justify-center pr-4">
          <div className="text-right">
            <p className="text-white text-2xl font-bold">
              {getGreeting()} {clientsStore.user.name}
            </p>
            <p className="text-white text-sm opacity-80">
              {clientsStore.user.role === rolesWithId.admin ? 'Administrator' : 'Financial Planner'}
            </p>
          </div>
        </div>
      </div>
      <div className="p-10">
        <div
          className="w-full mb-2"
          // onClick={() => navigate("/dashboard/AllAwakens")}
        >
          <p className="p-6 pb-3 flex flex-row items-center gap-3 font-semibold text-2xl">
            <span className="bg-yellow-200 flex items-center justify-center w-8 h-8 rounded-2xl">
              <IoBagCheckOutline className="text-blue-700 text-xl " />
            </span>
            All Awaken
          </p>
          <p className="px-6 pb-3 text-sm text-gray-600">
            {clientsStore.user.role === rolesWithId.admin 
              ? 'Overview of all plan types across all clients' 
              : 'Your assigned clients by plan type'
            }
          </p>
        </div>
        
        {/* Search Input */}
        <div className="px-6 mb-6">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search all clients by name, email, mobile, PAN, UID, or plan type..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery("");
                  setSearchResults([]);
                  setIsSearching(false);
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Search Results */}
        {(isSearching || searchQuery.trim()) && (
          <div className="px-6 mb-6">
            {searchLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Searching clients...</p>
              </div>
            ) : searchResults.length > 0 ? (
              <div className="bg-white shadow-xl rounded-lg overflow-hidden">
                <div className="p-4 bg-blue-50 border-b">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Search Results ({searchResults.length} {searchResults.length === 1 ? 'client' : 'clients'})
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Plan Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Mobile
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created On
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Financial Planner
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {searchResults.map((client) => {
                        const personal = client.awareness?.personalDetails || client.personalDetails || {};
                        const plan = client.awareness?.planDetails || client.planDetails || {};
                        const clientName = personal.name || personal.fullName || client.name || client.fullName || "N/A";
                        const planType = plan.planType || client.planType || "N/A";
                        const email = personal.email || client.email || "N/A";
                        const mobile = personal.phone || personal.mobile || personal.mobileNumber || client.phone || client.mobile || "N/A";
                        const financialPlanner = client.assignedTo?.name || client.createdBy?.name || "Unassigned";

                        return (
                          <tr key={client._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {clientName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {clientsStore.camelToTitleCase(planType)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {email}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {mobile}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDate(client.createdAt)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {financialPlanner}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                              <button
                                onClick={() => {
                                  if (planType && planType !== "N/A") {
                                    navigate(`/clients?planType=${encodeURIComponent(planType)}`);
                                  }
                                }}
                                className="text-blue-600 hover:text-blue-900 px-3 py-1 rounded hover:bg-blue-50"
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="bg-white shadow-xl rounded-lg p-8 text-center">
                <p className="text-gray-600">No clients found matching your search.</p>
              </div>
            )}
          </div>
        )}

        <div className="w-full grid grid-cols-3 gap-8">
          {clientsStore.planTypeMetrics.length > 0
            ? clientsStore.planTypeMetrics.map((element, index) => {
                console.log("element", JSON.stringify(element));
                return (
                  <div
                    key={index}
                    className="w-full shadow-xl rounded-lg bg-white p-4"
                    style={{ boxShadow: "0px 0px 30px 0px rgba(0,0,0,0.1)" }}
                    onClick={() => {
                      // Validate planType exists before navigation
                      if (!element.planType) {
                        console.error("PlanType is missing:", element);
                        alert("Invalid plan type. Please try again.");
                        return;
                      }
                      
                      if (element.count && element.count > 0) {
                        // Use React Router navigate for better SPA navigation
                        navigate(`/clients?planType=${encodeURIComponent(element.planType)}`);
                      } else {
                        clientsStore.user.role === "6720e9990ca50db671add43e"
                          ? alert(
                              "No clients assigned for this PlanType contact admin."
                            )
                          : alert("No clients available for this plan type.");
                      }
                    }}
                  >
                    <p className="pb-3 text-xl">
                      {clientsStore.camelToTitleCase(
                        element.planType || "Undecided"
                      )}
                    </p>
                    <div className="flex items-center justify-between">
                      <p className="text-4xl font-semibold">
                        {element.count || 0}
                      </p>
                      <img
                        src={fileDocument}
                        alt=""
                        className="w-10 h-auto text-secondary"
                      />
                      {/* <p className="text-4xl text-blue-500">
                        <PiFileTextLight className="" />
                      </p> */}
                    </div>
                  </div>
                );
              })
            : clientsStore.user.role === "6720e9990ca50db671add43e" &&
              planTypes.map((plan, index) => (
                <div
                  className="w-full shadow-xl rounded-lg bg-white p-4"
                  style={{ boxShadow: "0px 0px 30px 0px rgba(0,0,0,0.1)" }}
                  key={index}
                  onClick={() => alert("No data available contact admin")}
                >
                  <p className="pb-3 text-xl">
                    {clientsStore.camelToTitleCase(plan || "Undecided")}
                  </p>
                  <div className="flex items-center justify-between">
                    <p className="text-4xl font-semibold">{0}</p>
                    <img
                      src={fileDocument}
                      alt=""
                      className="w-10 h-auto text-blue-500"
                    />
                    {/* <p className="text-4xl text-blue-500">
                      <PiFileTextLight />
                    </p> */}
                  </div>
                </div>
              ))}
        </div>

        {/* <div className="w-full flex gap-7">
          <div
            className="w-[30%] h-[15%] shadow-lg rounded-lg bg-white p-4"
            onClick={() => navigate("/clients?planType=zeroToOne")}
          >
            <p className="pb-3 text-xl">Zero2One</p>
            <div className="flex items-center justify-between">
              <p className="text-4xl font-semibold">16</p>
              <p className="text-4xl text-blue-500">
                <CgFileDocument />
              </p>
            </div>
          </div>
          <div className="w-[30%] h-[15%] shadow-lg rounded-lg bg-white p-4">
            <p className="pb-3 text-xl">Basics</p>
            <div className="flex items-center justify-between">
              <p className="text-4xl font-semibold">16</p>
              <p className="text-4xl text-blue-500">
                <CgFileDocument />
              </p>
            </div>
          </div>
          <div className="w-[30%] h-[15%] shadow-lg rounded-lg bg-white p-4">
            <p className="pb-3 text-xl">Comprehensive</p>
            <div className="flex items-center justify-between">
              <p className="text-4xl font-semibold">16</p>
              <p className="text-4xl text-blue-500">
                <CgFileDocument />
              </p>
            </div>
          </div>
        </div>
        <div>
          <div
            className="w-[30%] h-[15%] shadow-lg rounded-lg bg-white p-4"
            onClick={() => navigate("/zero-to-one")}
          >
            <p className="pb-3 text-xl">Wealth</p>
            <div className="flex items-center justify-between">
              <p className="text-4xl font-semibold">16</p>
              <p className="text-4xl text-blue-500">
                <CgFileDocument />
              </p>
            </div>
          </div>
        </div> */}
        <div className="w-full flex justify-end bottom-2 right-6">
          <img
            src={AwakenFinancialJourneyLOGO}
            alt="AwakenFinancialJourneyLOGO"
            className="w-44 h-auto"
          />
        </div>
      </div>
    </div>
  );
}

export default AllAwakens;
